import { ArrowPathIcon, ClockIcon, IdentificationIcon } from '@heroicons/react/24/outline'
import { GlobalConfigType, setHeader } from 'actions'
import cloneDeep from 'clone-deep'
import { EmailValue, LoanPartiesEmailTo } from 'components/EmailTo'
import { LayoutLoading } from 'components/LayoutLoading'
import { Overview } from 'components/Overview'
import { BaseFile, COMPANY_DNS, COMPANY_TITLE, InputType, IS_COMPANY_CC, loanOptionsConstants } from 'config'
import { usePermissions } from 'hooks/usePermissions'
import { DateTime } from 'luxon'
import { loanOverviewAndStepLogic } from 'pages/LoanOverview/loanOverviewAndStepLogic'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Prompt, useHistory, useLocation, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  appraisalV2CreateLog,
  cancelAppraisalInfoV2,
  changeAppraisal2Status,
  createAppraisalInfoV2,
  deleteAppraisalInfoV2,
  getAppraisal2PaymentLink,
  getAppraisalInformationFeedV2,
  getAppraisalScopeJobAddons,
  getBorrowerEliteCredential,
  openS3Document,
  postAppraisal2PayOrInvoice,
  refreshVendorAppraisalOrders,
  sendOrderAppraisalFeasibilitySignRequest,
  setAppraisal2MessageRefresh,
  updateAppraisalInfoV2,
} from 'services'
import Api from 'services/api'
import { svgLoading } from 'stories/assets'
import { Button, DocumentFile, FormTable, InputFileTable, Modal, Select, TextArea, Tooltip } from 'stories/components'
import { CategoryDocument, CategoryDocuments } from 'stories/components/CategoryFileTable/CategoryFileTable'
import { InputFormTable } from 'stories/components/FormTable/InputFormTable'
import {
  confirm,
  formatDate,
  formatDateYMD,
  formatTime,
  InputValidate,
  insertAfter,
  openAuditLog,
  removeComma,
  REQUIRED_FIELD_ERROR_MESSAGE,
  validateEmail,
} from 'utils'
import { copyClipboard } from 'utils/copyClipboard'
import { setLoanNumber } from 'utils/setLoanNumber'
import { getActiveTimeZone } from 'utils/timezone'

import { defaultInputs, isShowEntityName } from './constants'
import {
  AppraisalNation,
  EliteValuation,
  handleFailedDocsNotification,
  Inspectify,
  RefreshResults,
  StandardAppraisalModal,
  Trinity,
} from './Modals'
import { normalizePaymentLinkEmails } from './paymentLinkEmails.util'
import {
  appraisal2IntegrationTypes,
  Appraisal2StatusType,
  Appraisal2ThirdPartyType,
  Appraisal2ThirdPartyTypeForInit,
  AppraisalDocumentCategories,
  AppraisalScopeThirdpartyTypes,
  appraisalScopeTitles,
  brokerCanNotEditFields,
  budgetReviewAmounts,
  budgetReviewOptions,
  cardFields,
  CLASS_VALUATION_ANALYSIS_ORDER_TYPE,
  contactPersonFields,
  feasibilityACHKeys,
  feasibilityCCKeys,
  feasibilityKeys,
  feasibilityOrderTypes,
  feasibilityRequiredKeys,
  feasibilityStudyAmounts,
  feasibilityStudyOptions,
  header,
  IntegrationType,
  integrationTypeTitles,
  integrationTypeTitlesReversed,
  IntegrationTypeValues,
  isClassValuationCvaProductName,
  mapLoanToAppraisal,
  mapOrderTypeFormOptions,
  nativeRushVendorTitles,
  OrderAppraisalOrderTypeOptions,
  OrderAppraisalOrderTypeOptionsByVendor,
  paymentInformationFormKeys,
  valueLinkVendorTitles,
  vendorToIntegrationType,
} from './types'

export default function AddAppraisalInformationV2({ child = false }: { child?: boolean }) {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<Array<any>>([])
  const [logs, setLogs] = useState<Array<any>>([])

  const [thirdpartyType, setThirdpartyType] = useState<string | null>(null)
  const [thirdpartyData, setThirdpartyData] = useState<any>(null)

  const [feed, setFeed] = useState<any>({})
  const [inputs, setInputs] = useState<any>({})
  const [editingLog, setEditingLog] = useState('')

  const [action, setAction] = useState('')
  const [editOrderID, setEditOrderID] = useState('All')
  const [orderIDs, setOrderIDs] = useState<Array<any>>([])
  const [cancelEditFlag, setCancelEditFlag] = useState(0)
  const [updateChangeMade, setUpdateChangeMade] = useState(false)
  const [docFiles, setDocFiles] = useState<Array<DocumentFile>>([])

  const [credentials, setCredentials] = useState<Record<IntegrationType, any[]>>()
  const [docTypes, setDocTypes] = useState<Record<IntegrationType, string[]>>()
  const [jobTypes, setJobTypes] = useState<Record<IntegrationType, string[]>>()
  const [additionalTypes, setAdditionalTypes] = useState<Record<IntegrationType, any>>()
  const [additionalInputs, setAdditionalInputs] = useState<Record<IntegrationType, Record<string, InputType>>>()
  const [jobTypeAddons, setJobTypeAddons] = useState<Record<IntegrationType, Record<string, string[]>>>({} as any)
  const [vendorErrors, setVendorErrors] = useState<Record<IntegrationType, string>>({} as any)
  const [tableColumns, setTableColumns] = useState<{ key: string; title: string; visible: boolean }[] | null>(null)
  const [paymentLinkLoading, setPaymentLinkLoading] = useState(false)
  const [refreshingOrders, setRefreshingOrders] = useState(false)
  const [showOrderProcess, setShowOrderProcess] = useState(true)
  const [refreshSummary, setRefreshSummary] = useState<any>(null)
  const [refreshSummaryAt, setRefreshSummaryAt] = useState(0)

  const [messageToModal, setMessageToModal] = useState(false)
  const [notifiers, setNotifiers] = useState<EmailValue>({})
  const [toEmails, setToEmails] = useState<any>([])
  const [appraiser, setAppraiser] = useState<Record<string, any>>({})
  const documentsRef = useRef()
  const formRef = useRef()
  const deepLinkAddOrderConsumedRef = useRef(false)
  const preferredPropertyAddressRef = useRef<string | null>(null)
  const pendingAddOrderRef = useRef(false)
  const shouldOpenAddFromDeepLinkRef = useRef(false)
  const dispatch = useDispatch()
  const location = useLocation()
  const history = useHistory()
  // Dedicated route uses :loan_number; ConfigurablePage (child) uses :loanNumber.
  const params = useParams<{ loan_number?: string; loanNumber?: string }>()
  const routeLoanNumber = params.loan_number || params.loanNumber
  // Set Api loan header during render so child effects do not fetch the wrong/empty loan
  // (same pattern as Servicing). Fall back to URL scrape only when the route has no param.
  const loanNo = (() => {
    const fromRoute = Number(routeLoanNumber)
    if (Number.isFinite(fromRoute) && fromRoute > 0) {
      if (Api.getLoanNumber() !== String(fromRoute)) setLoanNumber(fromRoute)
      return fromRoute
    }
    const fromHref = Number(setLoanNumber())
    return Number.isFinite(fromHref) && fromHref > 0 ? fromHref : 0
  })()

  const [upcomingStatus, setUpcomingStatus] = useState<null | Record<string, any>>(null)

  const { hasPermission } = usePermissions()

  const hasInternalPermission = hasPermission('Loan.Appraisals.Internal')
  const hasAdminPermission = hasPermission('Loan.Appraisals.Vendor')
  const hasCreateOrderPermission = hasPermission('Loan.Appraisals.CreateOrder')

  const [loan, { parties }, vendor] = useSelector((state: any) => [state.loan, state.loanDetail, state.vendor])

  const orderAppraisalVendorOptions = useSelector(
    (state: any) => state.globalConfigReducer[GlobalConfigType.ORDER_VALUATION],
  )

  useEffect(() => {
    if (!upcomingStatus) return
    changeStatus(upcomingStatus.id, upcomingStatus.value, upcomingStatus.additional)
    setUpcomingStatus(null)
  }, [upcomingStatus])

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const propertyAddress = `${params.get('propertyAddress') || ''}`.trim()
    const addOrder = params.get('addOrder') === '1'
    if (propertyAddress) preferredPropertyAddressRef.current = propertyAddress
    if (addOrder) pendingAddOrderRef.current = true
  }, [])

  useEffect(() => {
    if (!shouldOpenAddFromDeepLinkRef.current) return
    if (!Object.keys(inputs || {}).length) return
    if (!inputs.propertyAddress) return
    shouldOpenAddFromDeepLinkRef.current = false
    // Defer so FormTable can sync parent `_inputs` before opening add mode
    const timer = window.setTimeout(async () => {
      const form = formRef.current as any
      if (!form?.openAdd) return
      await form.openAdd()
    }, 100)
    return () => window.clearTimeout(timer)
  }, [inputs])

  const clearOrderValuationDeepLinkParams = () => {
    const params = new URLSearchParams(location.search)
    if (!params.has('propertyAddress') && !params.has('addOrder')) return
    params.delete('propertyAddress')
    params.delete('addOrder')
    const search = params.toString()
    history.replace({ pathname: location.pathname, search: search ? `?${search}` : '' })
  }

  const normalizePropertyAddresses = (raw: unknown): string[] => {
    if (Array.isArray(raw)) {
      return raw.map((addr) => `${addr || ''}`.trim()).filter(Boolean)
    }
    const single = `${raw || ''}`.trim()
    return single ? [single] : []
  }

  const resolveDefaultPropertyAddresses = (): string[] => {
    const preferred = `${preferredPropertyAddressRef.current || ''}`.trim()
    const addresses: string[] = Array.isArray(feed.propertyAddresses) ? feed.propertyAddresses : []
    if (preferred) {
      const matched = addresses.find((addr) => addr.toLowerCase() === preferred.toLowerCase())
      return [matched || preferred]
    }
    if (addresses.length) return [...addresses]
    const subject = `${feed.subjectPropertyAddress || ''}`.trim()
    return subject ? [subject] : []
  }

  const onSendSignLink = async (id: number, feasibilityData: Record<string, any>) => {
    if (removeComma(feasibilityData.feasibilityCCStudyFee) <= 0) {
      return toast('Payment Amount is Required!', { type: 'error' })
    }

    if (mapOrderTypeFormOptions[feasibilityData.orderType]?.indexOf(feasibilityData.appraisalForm) === -1) {
      return toast(`'Appraisal Form' is Required!`, { type: 'error' })
    }
    if (!validateEmail(feasibilityData.feasibilityEmailToLink)) {
      return toast(`Email to Send Link is Required!`, { type: 'error' })
    }
    if (!id) {
      id = await (formRef.current as any).submit()
      if (!id) return
    }
    setLoading(true)
    await sendOrderAppraisalFeasibilitySignRequest(id, feasibilityData)

    const orderTitle = getOrderTitle(feasibilityData.orderType)
    setUpcomingStatus({ id, value: 'pendingPayment', additional: `${orderTitle} Payment Requested` })
    toast(`${orderTitle} Payment Request is sent.`, { type: 'info' })
    setLoading(false)
  }

  const cancelOrder = async () => {
    if (!editOrderID || editOrderID === 'All') {
      return toast('Order ID is required!', { type: 'error' })
    }

    const content = (
      <div className="text-gray-400 mb-4 text-[18px]">
        Are you sure want to cancel order?
        <br />
        <span className="text-gray-600 text-[15px] font-bold">Order ID: {editOrderID}</span>
      </div>
    )
    const result = await confirm(content)
    if (!result) return

    onConfirmCancelOrder()
  }

  const onChangeEmail = (data: EmailValue) => {
    setNotifiers(data)
  }

  const createLog = async (fromModal: boolean) => {
    if (editingLog.length === 0) {
      return toast('Message is Required!', { type: 'error' })
    }

    const isGeneralNote = !editOrderID || editOrderID === 'All'

    if (!fromModal && hasInternalPermission) {
      let temp: any = {},
        emails = []
      if (isGeneralNote) {
        if (feed.loanOfficer) {
          temp[feed.loanOfficer] = 'to'
          emails.push({ party: 'Loan Officer', email: feed.loanOfficer })
        }
        if (feed.loanProcessor) {
          temp[feed.loanProcessor] = 'to'
          emails.push({ party: 'Loan Processor', email: feed.loanProcessor })
        }
      } else {
        const edit = data.find((item) => item.id == editOrderID)
        temp[edit.createdBy] = 'to'
        emails.push({ party: 'Order Creator', email: edit.createdBy })
        temp[edit.info.loanOfficerEmail] = 'to'
        emails.push({ party: 'Order Loan Officer', email: edit.info.loanOfficerEmail })

        if (edit.info.processorEmail) {
          temp[edit.info.processorEmail] = 'to'
          emails.push({ party: 'Order Loan Processor', email: edit.info.processorEmail })
        }
        if (appraiser) emails.push({ party: 'Appraiser', email: appraiser })
      }
      setToEmails(emails)
      setNotifiers(temp)
      setMessageToModal(true)
      return true
    }
    setAction('createLog')
    setLoading(true)
    const res = await appraisalV2CreateLog({
      id: isGeneralNote ? null : editOrderID,
      message: editingLog,
      files: docFiles,
      notifiers,
    })
    if (res.success) {
      let temp = cloneDeep(logs)
      temp.push(res.data)
      setLogs(temp)
    }
    setEditingLog('')
    setDocFiles([])
    setAction('')
    setLoading(false)
    setMessageToModal(false)
  }

  const renderDocuments = (value: CategoryDocument[] = [], onChange: Function, input: InputType) => {
    const integrationType = (input as any).integrationType as IntegrationType
    const types = !docTypes || !integrationType ? null : docTypes[integrationType]

    return (
      <CategoryDocuments
        ref={documentsRef}
        documents={value}
        onChange={onChange}
        setLoading={setLoading}
        type={'OrderAppraisal'}
        categories={AppraisalDocumentCategories}
        types={types}
      />
    )
  }

  const getOrderTitle = (orderType: string) => {
    return orderType.split(' ')[0]
  }

  const inputLogic = (_data: any, id: number = 0, key: string = '', values: Record<string, any> = {}) => {
    if (id > 0 && key !== '') {
      setUpdateChangeMade(true)
    }
    // Keep the notes list on the order being edited. Saving/cancelling must reset this to All
    // (see onSubmit / onCancel) or other orders' notes stay hidden after the form closes.
    if (id > 0 && String(editOrderID) !== String(id)) {
      setEditOrderID(String(id))
    }
    const orderRecords = [...data]
    let formData = cloneDeep(_data)

    if (formData.paymentLinkEmail) {
      formData.paymentLinkEmail.value = normalizePaymentLinkEmails(formData.paymentLinkEmail.value)
    }

    if (formData.propertyAddress) {
      const fromValues = values.propertyAddress !== undefined ? values.propertyAddress : formData.propertyAddress.value
      let addresses = normalizePropertyAddresses(fromValues)
      // Existing orders always store/send a single address to third-party APIs
      if (id > 0 && addresses.length > 1) addresses = addresses.slice(0, 1)
      formData.propertyAddress.value = addresses
    }

    const amc = formData.amc.value
    const integrationType = integrationTypeTitlesReversed[amc] as IntegrationType
    const clientLenderAccount = formData.clientLenderAccount.value

    if (integrationType && additionalInputs && additionalInputs[integrationType]) {
      const elements = additionalInputs[integrationType]
      Object.keys(elements)
        .reverse()
        .forEach((key) => {
          formData = insertAfter(formData, 'underConstruction', key, elements[key])
          if (values[key] !== undefined && values[key] !== null) {
            formData[key].value = values[key]
            formData[key].error = InputValidate(formData[key])
          }
        })
    } else {
      const defaultKeys = Object.keys(defaultInputs())
      Object.keys(formData).forEach((key) => {
        if (!defaultKeys.includes(key)) delete formData[key]
      })
    }

    if (key == 'amc') {
      if (integrationType && vendorErrors[integrationType]) {
        toast(`${amc}: ${vendorErrors[integrationType]}`, { type: 'error', autoClose: false })
      }
      if (Object.keys(OrderAppraisalOrderTypeOptionsByVendor).includes(_data.amc.value)) {
        formData.orderType.value = ''
        formData.orderType.options = OrderAppraisalOrderTypeOptionsByVendor[_data.amc.value]
      } else if (additionalTypes?.[integrationType]?.[clientLenderAccount || 'main']?.orderType) {
        formData.orderType.value = ''
        formData.orderType.options = additionalTypes?.[integrationType]?.[clientLenderAccount || 'main']?.orderType
      } else formData.orderType.options = OrderAppraisalOrderTypeOptions
    } else if (Object.keys(OrderAppraisalOrderTypeOptionsByVendor).includes(amc)) {
      // Keep vendor-specific order types on edit/load (not only when AMC changes).
      formData.orderType.options = OrderAppraisalOrderTypeOptionsByVendor[amc]
    } else if (additionalTypes?.[integrationType]?.[clientLenderAccount || 'main']?.orderType) {
      formData.orderType.options = additionalTypes?.[integrationType]?.[clientLenderAccount || 'main']?.orderType
    } else if (amc) {
      formData.orderType.options = OrderAppraisalOrderTypeOptions
    }

    const orderType = formData.orderType.value
    const isFeasibility = feasibilityOrderTypes.includes(orderType)
    if (isFeasibility) {
      formData.feasibilityTitle.title = `${getOrderTitle(orderType)} Payment Method`
    }
    paymentInformationFormKeys.forEach((key) => (formData[key].visible = !isFeasibility))
    let isSendInvoice = formData.isSendInvoice.value
    if (isSendInvoice === undefined) isSendInvoice = true
    if (!isFeasibility) {
      cardFields.map((key) => {
        formData[key].visible = !isSendInvoice
      })
      formData['paymentLinkEmail'].visible = isSendInvoice
    }
    formData['isSendInvoice'].span = isSendInvoice ? 2 : 4

    if (!hasInternalPermission) formData['paymentAmount'].visible = false

    if (formData['contactSameAsBorrower'].value) {
      Object.keys(contactPersonFields).map((key) => {
        const map = contactPersonFields[key]
        try {
          formData[key].value = formData[map].value
          formData[key].error = InputValidate(formData[key])
        } catch {}
      })
    }

    formData.purchaseAmount.visible = formData.purpose.value === 'Purchase'
    formData.rush.tooltip = !amc
      ? 'Flags the order as a rush internally. Select a vendor to also notify the AMC.'
      : nativeRushVendorTitles.includes(amc)
      ? `${amc} receives this as a rush flag on the order.`
      : `${amc} is notified with a rush banner on the order notes sent with this order.`
    formData.appraisalScopeJobAddons.visible = false
    formData.valueLinkAdditionalItems.visible = false
    formData.appraisalForm.visible = amc !== 'Inspectify'
    formData.appraisalForm.required = amc !== 'Inspectify'
    if (amc === 'Inspectify') {
      formData.appraisalForm.value = ''
      formData.appraisalForm.error = ''
    }

    const isInspectifyDraw = amc === 'Inspectify' && orderType === 'draw_inspection'
    const inspectifyDrawKeys = [
      'goAnytime',
      'accessType',
      'accessCode',
      'accessDirections',
      'inspectionScopeNumber',
      'inspectionScopeItems',
    ]
    inspectifyDrawKeys.forEach((drawKey) => {
      if (!formData[drawKey]) return
      formData[drawKey].visible = isInspectifyDraw
      if (!isInspectifyDraw) {
        formData[drawKey].error = ''
        formData[drawKey].required = false
      }
    })
    if (formData.accessType) {
      formData.accessType.required = isInspectifyDraw
      if (isInspectifyDraw) formData.accessType.error = InputValidate(formData.accessType)
    }
    const accessNeedsCodeAndDirections = ['door_code', 'lockbox', 'combination'].includes(formData.accessType?.value)
    if (formData.accessCode) {
      formData.accessCode.required = isInspectifyDraw && accessNeedsCodeAndDirections
      formData.accessCode.error =
        isInspectifyDraw && accessNeedsCodeAndDirections ? InputValidate(formData.accessCode) : ''
    }
    if (formData.accessDirections) {
      formData.accessDirections.required =
        isInspectifyDraw && (accessNeedsCodeAndDirections || formData.accessType?.value === 'other')
      formData.accessDirections.error =
        isInspectifyDraw && (accessNeedsCodeAndDirections || formData.accessType?.value === 'other')
          ? InputValidate(formData.accessDirections)
          : ''
    }
    if (formData.inspectionScopeItems) {
      formData.inspectionScopeItems.required = isInspectifyDraw && !id
      const scopeValue = Array.isArray(formData.inspectionScopeItems.value) ? formData.inspectionScopeItems.value : []
      if (isInspectifyDraw && !id) {
        const validScope = scopeValue.some(
          (row: any) => String(row?.name || '').trim() && String(row?.description || '').trim(),
        )
        formData.inspectionScopeItems.error = validScope
          ? ''
          : 'At least one scope item with name and description is required'
      } else {
        formData.inspectionScopeItems.error = ''
      }
      const scopeItemInputs: Record<string, InputType> = {
        name: { inputType: 'text', title: 'Name', value: '', required: true },
        description: { inputType: 'textarea', title: 'Description', value: '', required: true, rows: 2 },
        budget: { inputType: 'text', type: 'number', title: 'Budget', value: '' },
        previousDisbursement: { inputType: 'text', type: 'number', title: 'Previous Disbursement', value: '' },
      }
      formData.inspectionScopeItems.render = (value: any[] = [], onChange: (rows: Record<string, any>[]) => void) => (
        <InputFormTable
          inputs={scopeItemInputs}
          data={Array.isArray(value) ? value : []}
          onChange={onChange}
          cols={2}
          error={formData.inspectionScopeItems.error}
        />
      )
    }

    let appraisalFormOptions = []
    formData.propertyType.options = loanOptionsConstants.propertyType

    const additionalOptions = additionalTypes?.[integrationType]?.[clientLenderAccount || 'main']
    if (additionalOptions) {
      Object.keys(additionalOptions).forEach((key) => {
        if (!formData[key]) return
        formData[key].options = additionalOptions[key]
      })
    }

    formData.documents.integrationType = integrationType

    let appraisalFormSort = true
    try {
      if (valueLinkVendorTitles.includes(amc)) {
        if (clientLenderAccount) {
          appraisalFormOptions = (jobTypes as any)[integrationType][clientLenderAccount][orderType] || []
        }

        // Every ValueLink vendor takes AppraisalType2..4 on create, from this same list, so the
        // selector belongs to all of them. It used to be shown for Vision VMC alone, which left
        // AMC Direct with no way to order a second product on one order.
        if (orderType && clientLenderAccount && appraisalFormOptions.length) {
          formData.valueLinkAdditionalItems.visible = true
          formData.valueLinkAdditionalItems.options = appraisalFormOptions.filter(
            (v: string) => v !== formData.appraisalForm.value,
          )
        }

        if (orderType && clientLenderAccount) {
          const propertyTypes = additionalTypes?.[integrationType]?.[clientLenderAccount]?.['propertyTypes']
          formData.propertyType.options = propertyTypes?.[orderType] || propertyTypes?.['Residential Appraisal']
        } else {
          formData.propertyType.options = []
          formData.propertyType.value = ''
        }
      } else {
        if (orderType == 'Residential Appraisal') {
          if (amc === integrationTypeTitles[IntegrationType.Arivs]) {
            appraisalFormOptions = Object.keys((jobTypes as any)?.[integrationType]?.[orderType] || {})
            appraisalFormSort = false
          } else if (amc === integrationTypeTitles[IntegrationType.ClassValuation]) {
            appraisalFormOptions = (jobTypes as any)?.[integrationType]?.[clientLenderAccount || 'main'] || []
            appraisalFormSort = false
          } else if (appraisalScopeTitles.includes(amc)) {
            if (clientLenderAccount) {
              appraisalFormOptions = (jobTypes as any)[integrationType][clientLenderAccount] || []
              formData.appraisalScopeJobAddons.visible = true

              const jobType = formData.appraisalForm.value
              if (jobType) {
                let addons = jobTypeAddons?.[integrationType]?.[jobType]
                formData.appraisalScopeJobAddons.isLoading = !addons

                if (!addons) {
                  addons = []
                  onUpdateAppraisalScopeJobType(integrationType, clientLenderAccount, jobType)
                }
                formData.appraisalScopeJobAddons.options = addons
              }
            }
          } else appraisalFormOptions = mapOrderTypeFormOptions[orderType]
        } else if (
          amc === integrationTypeTitles[IntegrationType.ClassValuation] &&
          orderType === CLASS_VALUATION_ANALYSIS_ORDER_TYPE
        ) {
          const catalog = (jobTypes as any)?.[integrationType]?.[clientLenderAccount || 'main'] || []
          appraisalFormOptions = (catalog as string[]).filter((name) => isClassValuationCvaProductName(name))
          // If catalog names don't match the CVA regex, still offer the order-type label so
          // submit can use backend name-based product resolution.
          if (!appraisalFormOptions.length) {
            appraisalFormOptions = [CLASS_VALUATION_ANALYSIS_ORDER_TYPE]
          }
          appraisalFormSort = false
        } else appraisalFormOptions = mapOrderTypeFormOptions[orderType]
      }
    } catch (e) {
      console.log(e)
    }
    formData.appraisalForm.options = appraisalFormOptions || []
    formData.appraisalForm.sort = appraisalFormSort
    formData.appraisalForm.title = 'Appraisal Form'
    if (
      formData.appraisalForm.value &&
      formData.appraisalForm.options.length &&
      !formData.appraisalForm.options.includes(formData.appraisalForm.value)
    ) {
      formData.appraisalForm.value = ''
      formData.appraisalForm.error = ''
    }

    formData.underConstruction.visible = ['Commercial Appraisal', 'Residential Appraisal'].indexOf(orderType) !== -1

    if (isFeasibility) {
      ;[...feasibilityKeys, ...feasibilityACHKeys, ...feasibilityCCKeys, 'feasibilitySignKey'].forEach(
        (key) => (formData[key].visible = false),
      )
      formData.feasibilityTitle.visible = true
      formData.feasibilityEmailToLink.visible = true
      formData.feasibilitySignKey.visible = true
      formData.feasibilityCCStudyFee.visible = true
      feasibilityRequiredKeys.forEach((key) => (formData[key].required = true))

      if (key === 'appraisalForm') {
        let amount = 0
        const appraisalForm = formData.appraisalForm.value
        if (appraisalForm) {
          let index = feasibilityStudyOptions.indexOf(appraisalForm)
          if (index != -1) {
            amount = feasibilityStudyAmounts[index]
          } else {
            const index = budgetReviewOptions.indexOf(appraisalForm)
            if (index != -1) {
              amount = budgetReviewAmounts[index]
            }
          }
        }
        formData.feasibilityCCStudyFee.value = amount
      }

      const feasibilityData: Record<string, any> = {
        feasibilityEmailToLink: formData.feasibilityEmailToLink.value,
        orderType: formData.orderType.value,
        appraisalForm: formData.appraisalForm.value,
        feasibilityCCStudyFee: formData.feasibilityCCStudyFee.value,
      }
      formData.feasibilitySignKey.render = () => (
        <Button color="gray" onClick={() => onSendSignLink(id, feasibilityData)}>
          Send Payment Link
        </Button>
      )

      if (hasAdminPermission) {
        formData['feasibilityCCStudyFee'].disabled = false
      } else {
        formData['feasibilityCCStudyFee'].disabled = true
      }
    } else {
      ;[...feasibilityKeys, ...feasibilityACHKeys, ...feasibilityCCKeys, 'feasibilitySignKey'].forEach(
        (key) => (formData[key].visible = false),
      )
    }
    formData.feasibilitySignKey.visible = isFeasibility && hasInternalPermission
    formData.underConstructionAlert.visible =
      formData.underConstruction.visible === true && formData.underConstruction.value === true

    formData.documents.render = renderDocuments

    Object.keys(vendorToIntegrationType).forEach((vendorKey) => {
      if (vendor[vendorKey]) return
      const integrationType = vendorToIntegrationType[vendorKey]
      const { options } = formData.amc
      delete options[(integrationTypeTitles as any)[integrationType]]
    })
    // Trinity is not an IntegrationType vendor — gate its AMC option on VendorKey.Trinity
    if (!vendor.Trinity) {
      delete formData.amc.options.Trinity
    }

    if (formData.amc.visible) {
      if (
        Object.values(appraisal2IntegrationTypes)
          .map((v) => integrationTypeTitles[v])
          .includes(formData.amc.value)
      ) {
        formData.entityName.visible = true
        if (!formData.entityName.value) formData.entityName.value = feed.entityTitle
      } else {
        formData.entityName.visible = false
      }
      if (formData.amc.value === integrationTypeTitles[IntegrationType.EliteValuation]) {
        formData.processorEmail.required = true
      } else {
        formData.processorEmail.required = false
        formData.processorEmail.error = ''
      }
      if (formData.amc.value === 'Trinity') {
        formData.trinityOrderID.visible = true
      } else {
        formData.trinityOrderID.visible = false
      }
    }

    if (
      credentials &&
      (IntegrationTypeValues as string[]).includes(integrationType) &&
      amc != integrationTypeTitles[IntegrationType.Arivs] &&
      ((['Residential Appraisal', CLASS_VALUATION_ANALYSIS_ORDER_TYPE].includes(formData.orderType.value) &&
        Object.values(integrationTypeTitles).includes(amc)) ||
        valueLinkVendorTitles.includes(amc))
    ) {
      const credential = credentials[integrationType]

      formData.dividerClientLender.visible = true
      formData.clientLenderAccount.visible = true
      formData.clientLenderCompany.visible = true

      const accountOptions: any = {}
      credential.map((v: any) => {
        if (v.value.type === 'main') accountOptions['main'] = COMPANY_TITLE
        if (v.value.type === 'broker') accountOptions['broker'] = `Broker (${parties.broker.email})`
        if (v.value.type === 'creator') accountOptions['creator'] = `Loan Creator (${parties.creator.email})`
        if (v.value.type === 'loanOfficer')
          accountOptions['loanOfficer'] = `Loan Officer (${parties.loanOfficer.email})`
        if (v.value.type === 'loanProcessor')
          accountOptions['loanProcessor'] = `Loan Processor (${parties.loanProcessor.email})`
        if (v.value.type === 'myAccount') accountOptions['myAccount'] = 'My Account'
      })
      const accountEmpty = !`${formData.clientLenderAccount.value || ''}`.trim()
      const companyEmpty = !`${formData.clientLenderCompany.value || ''}`.trim()
      if (key === 'amc' && accountEmpty && companyEmpty && accountOptions.main) {
        formData.clientLenderAccount.value = 'main'
        formData.clientLenderAccount.error = ''
      }
      formData.clientLenderAccount.options = accountOptions

      const options: any = {}
      credential
        .filter((v: any) => v.value.type == formData.clientLenderAccount.value)
        .map((v: any) => {
          if (v.value.clientid) options[v.value.clientid] = v.desc
          else options[v.value.type] = v.desc
        })
      formData.clientLenderCompany.options = options
      if (key === 'amc' && accountEmpty && companyEmpty) {
        const firstCompany = Object.keys(options)[0]
        if (firstCompany) {
          formData.clientLenderCompany.value = firstCompany
          formData.clientLenderCompany.error = ''
        }
      }
    } else {
      formData.dividerClientLender.visible = false
      formData.clientLenderAccount.visible = false
      formData.clientLenderCompany.visible = false
    }

    if (formData.appraisalForm.value === 'Draw Inspection') {
      formData.dividerPay.visible = false
      formData.isSendInvoice.visible = false
      formData.paymentLinkEmail.visible = false
    }

    if (amc === integrationTypeTitles[IntegrationType.ClassValuation]) {
      formData.isSendInvoice.visible = false
    }

    if (formData.notifications.value?.length > 1) {
    } else if (amc !== integrationTypeTitles[IntegrationType.AMCDirect]) {
      formData.notifications.value =
        formData.appraisalForm.value === 'Draw Inspection'
          ? [`draws@${COMPANY_DNS}`]
          : [`${IS_COMPANY_CC ? 'processing' : 'loansetup'}@${COMPANY_DNS}`]
    }

    const showEntityName = isShowEntityName(loan.borrowerType ?? feed.borrowerType, amc)
    if (valueLinkVendorTitles.includes(amc)) {
      formData.borrowerFirstName.visible = !showEntityName
      formData.borrowerLastName.visible = !showEntityName
      formData.contactSameAsBorrower.visible = !showEntityName
    }
    formData.entityName.visible = showEntityName

    const supportsMessageRefresh = valueLinkVendorTitles.includes(amc)
    const isAmcDirect = amc === integrationTypeTitles[IntegrationType.AMCDirect]
    if (formData.noteMessages) {
      formData.noteMessages.required = isAmcDirect
      if (!isAmcDirect) {
        formData.noteMessages.error = ''
      } else if (`${formData.noteMessages.value || ''}`.trim()) {
        formData.noteMessages.error = ''
      } else if (`${formData.noteMessages.value || ''}`.length) {
        formData.noteMessages.error = REQUIRED_FIELD_ERROR_MESSAGE
      }
    }
    if (formData.refreshMessages) {
      formData.refreshMessages.visible = supportsMessageRefresh
      if (key === 'amc' && id === 0) {
        formData.refreshMessages.value = supportsMessageRefresh
      }
      if (!supportsMessageRefresh) {
        formData.refreshMessages.value = false
      }
      const orderRowForRefresh = id > 0 ? orderRecords.find((item: any) => item.id == id) : null
      if (orderRowForRefresh && ['completed', 'cancelled'].includes(orderRowForRefresh.status)) {
        formData.refreshMessages.disabled = true
        formData.refreshMessages.value = false
      }
    }
    if (formData.amcDirectPaymentActions) formData.amcDirectPaymentActions.visible = false
    if (formData.amcDirectPaymentLink) formData.amcDirectPaymentLink.visible = false
    if (isAmcDirect) {
      const orderRow = id > 0 ? orderRecords.find((item: any) => item.id == id) : null
      const isPendingPayment = orderRow?.status === 'pendingPayment'
      if (formData.appraisalCost) formData.appraisalCost.disabled = true
      formData.isSendInvoice.value = false
      if (formData.notifications) {
        formData.notifications.visible = false
        if (id === 0) formData.notifications.value = []
      }
      paymentInformationFormKeys.forEach((k) => {
        if (formData[k]) formData[k].visible = false
      })
      cardFields.forEach((k) => {
        if (formData[k]) formData[k].visible = false
      })
      if (isPendingPayment) {
        formData.dividerPay.visible = true
        formData.dividerPay.title = 'Payment Link'
        formData.paymentLinkEmail.visible = true
        formData.paymentLinkEmail.required = false
        formData.paymentLinkEmail.title = 'Emails to Send Link (optional)'
        formData.amcDirectPaymentLink.visible = true
        formData.amcDirectPaymentLink.value =
          orderRow?.thirdPartyAPI?.paymentLink || formData.amcDirectPaymentLink.value || ''
        formData.amcDirectPaymentActions.visible = true
        formData.amcDirectPaymentActions.render = () => (
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-sm text-gray-500">AMC Direct emails the borrower on this order.</span>
            <Button
              color="gray"
              loading={paymentLinkLoading}
              onClick={() => sendAmcDirectPaymentLink(id, formData.paymentLinkEmail.value)}
            >
              Send Payment Link
            </Button>
            <Button link onClick={() => copyAmcDirectPaymentLink(id, formData.amcDirectPaymentLink.value)}>
              {formData.amcDirectPaymentLink.value ? 'Copy Link' : 'Load & Copy Link'}
            </Button>
          </div>
        )
      }
    }

    if (key === 'refreshMessages' && id > 0) {
      const next = !!formData.refreshMessages?.value
      setAppraisal2MessageRefresh({ id, refreshMessages: next }).then((res) => {
        if (res?.success) {
          setData((prev) =>
            prev.map((item) =>
              item.id == id
                ? {
                    ...item,
                    info: { ...item.info, refreshMessages: next },
                    thirdPartyAPI: res.data?.thirdPartyAPI || item.thirdPartyAPI,
                  }
                : item,
            ),
          )
        } else {
          toast(res?.message || 'Failed to update Refresh Messages.', { type: 'error' })
          ;(formRef.current as any)?.updateInputs?.({
            refreshMessages: { value: !next },
          })
        }
      })
    }

    return formData
  }

  const loadAmcDirectPaymentLink = async (orderId: number) => {
    setPaymentLinkLoading(true)
    try {
      const res = await getAppraisal2PaymentLink(orderId)
      const link = res?.paymentLink || ''
      if (link) {
        setData((prev) =>
          prev.map((item) =>
            item.id == orderId ? { ...item, thirdPartyAPI: { ...item.thirdPartyAPI, paymentLink: link } } : item,
          ),
        )
        ;(formRef.current as any)?.updateInputs?.({
          amcDirectPaymentLink: { value: link },
        })
      }
      return link
    } finally {
      setPaymentLinkLoading(false)
    }
  }

  const copyAmcDirectPaymentLink = async (orderId: number, currentLink?: string) => {
    const link = currentLink || (await loadAmcDirectPaymentLink(orderId))
    if (!link) {
      toast('Payment link is not available yet from AMC Direct.', { type: 'warning' })
      return
    }
    copyClipboard(link)
  }

  const sendAmcDirectPaymentLink = async (orderId: number, paymentLinkEmail: any) => {
    const emails = normalizePaymentLinkEmails(paymentLinkEmail)
    setPaymentLinkLoading(true)
    try {
      const res = await postAppraisal2PayOrInvoice({
        id: orderId,
        info: { isSendInvoice: true, ...(emails.length ? { paymentLinkEmail: emails } : {}) },
      })
      if (res?.success) {
        toast(res.message || 'Payment link sent.', { type: 'success' })
        if (res.paymentLink) {
          setData((prev) =>
            prev.map((item) =>
              item.id == orderId
                ? { ...item, thirdPartyAPI: { ...item.thirdPartyAPI, paymentLink: res.paymentLink } }
                : item,
            ),
          )
          ;(formRef.current as any)?.updateInputs?.({
            amcDirectPaymentLink: { value: res.paymentLink },
          })
        }
      }
    } finally {
      setPaymentLinkLoading(false)
    }
  }

  const onUpdateAppraisalScopeJobType = async (
    integrationType: IntegrationType,
    lenderAccount: string,
    jobType: string,
  ) => {
    const addons = await getAppraisalScopeJobAddons({ integrationType, lenderAccount, jobType })
    const newJobTypeAddons = cloneDeep(jobTypeAddons)
    if (!newJobTypeAddons?.[integrationType]) newJobTypeAddons[integrationType] = {}
    newJobTypeAddons[integrationType][jobType] = addons

    setJobTypeAddons(newJobTypeAddons)
    ;(formRef.current as any).updateInputs({
      appraisalScopeJobAddons: {
        options: addons,
        isLoading: false,
      },
    })
  }

  useEffect(() => {
    if (!feed.loanNumber) return

    onFeedUpdate()
  }, [feed])

  const onFeedUpdate = async () => {
    const temp: any = defaultInputs()
    let _loanNumber = feed.loanNumber
    if (feed.byteproFileName !== 0) _loanNumber = feed.byteproFileName
    temp.loanNumber.value = _loanNumber
    temp.loanOfficerEmail.value = feed.loanOfficer
    temp.processorEmail.value = feed.loanProcessor
    temp.orderDate.value = formatDateYMD()
    temp.clientDueDate.value = DateTime.now().setZone(getActiveTimeZone()).plus({ days: 7 }).toFormat('yyyy-MM-dd')
    temp.entityName.value = feed.entityTitle
    temp.borrowerFirstName.value = feed.firstName
    temp.borrowerLastName.value = feed.lastName
    temp.borrowerEmail.value = feed.email
    temp.borrowerPhone.value = feed.phone
    temp.propertyAddress.value = resolveDefaultPropertyAddresses()
    temp.propertyCounty.value = feed.propertyCounty
    temp.propertyType.value = feed.propertyType
    temp.purchaseAmount.value = feed.propertyPurchasePrice
    temp.purpose.value = feed.transactionType
    temp.occupancy.value = mapLoanToAppraisal.occupancy[feed.proposedOccupancy]
    brokerCanNotEditFields.map((key: string) => {
      temp[key].disabled = !hasInternalPermission
    })
    temp.amc.visible = hasAdminPermission
    temp.trinityOrderID.visible = hasAdminPermission
    temp.notifications.visible = hasAdminPermission
    temp.entityName.visible = false
    const nextInputs = await inputLogic(temp)
    nextInputs.paymentLinkEmail.value = []
    setInputs(nextInputs)

    if (pendingAddOrderRef.current && !deepLinkAddOrderConsumedRef.current) {
      deepLinkAddOrderConsumedRef.current = true
      pendingAddOrderRef.current = false
      preferredPropertyAddressRef.current = null
      clearOrderValuationDeepLinkParams()
      shouldOpenAddFromDeepLinkRef.current = true
    }
  }

  const onAddPress = async () => {
    setEditOrderID('All')
    ;(formRef.current as any)?.updateInputs({
      paymentLinkEmail: { value: [] },
      contactSameAsBorrower: { value: false },
      contactFirstName: { value: '' },
      contactLastName: { value: '' },
      contactEmail: { value: '' },
      contactPhone: { value: '' },
    })
    return true
  }

  const changeStatus = async (id: number, value: string, additional: string = '') => {
    setLoading(true)
    setLoanNumber()
    const res = await changeAppraisal2Status({ id, value, additional })
    if (!additional) toast('Successfully Updated!', { type: 'success' })
    setLoading(false)
    if (res.success) {
      let temp = cloneDeep(data)
      temp.map((item, index) => {
        if (item.id === id) {
          temp[index].status = value
        }
      })
      dispatch(
        setHeader({
          activeChangeFlag: true,
        }),
      )
      setData(temp)
    }
  }

  const showHistory = (id: number, key: string = 'status') => {
    const options = {
      table: 'Appraisal2',
      field: key,
      keys: {
        field: key,
        id,
      },
    }
    openAuditLog(options)
  }

  const renderStatus = (item: any) => {
    const load = action === `status-change-${item.id}`
    return (
      <div className="flex items-center">
        <div className={`w-[160px] -mb-4`}>
          <Select
            key={item.id}
            id={`loan-status-${item.id}`}
            size={3}
            title=""
            options={Appraisal2StatusType}
            disabled={load}
            value={item.status}
            onChange={(value) => changeStatus(item.id, value)}
          />
        </div>
        <span className="ml-2 text-gray-500 cursor-pointer">
          <ClockIcon className="w-4 h-4" onClick={() => showHistory(item.id)} />
        </span>
      </div>
    )
  }

  const getHeader = useMemo(() => {
    const catalog = tableColumns?.length
      ? tableColumns.filter((c) => c.visible)
      : header().map((h) => ({ key: h.key, title: typeof h.title === 'string' ? h.title : h.key, visible: true }))

    let cols = catalog.map((c) => {
      const fromDefault = header().find((h) => h.key === c.key)
      return {
        key: c.key,
        title: fromDefault?.title ?? c.title,
      }
    })

    if (!hasAdminPermission) {
      cols = cols.filter((c) => c.key !== 'vendor')
    }
    return cols
  }, [hasAdminPermission, tableColumns])

  const getData = useMemo(() => {
    let rlt: any = []

    const getVendorFragment = (item: any) => {
      const { amc } = item.info
      let rlt: any = [<div>{orderAppraisalVendorOptions[amc] || amc}</div>]
      const type = item.thirdPartyAPI?.type
      let identifier = ''

      if (AppraisalScopeThirdpartyTypes.includes(type)) {
        identifier = item.thirdPartyAPI.appraisalFileNumber
      }
      if (
        [Appraisal2ThirdPartyType.Trinity, Appraisal2ThirdPartyType.Inspectify].includes(type) &&
        item.thirdPartyAPI.orderID
      ) {
        const prefix = {
          [Appraisal2ThirdPartyType.Trinity]: 'TR',
          [Appraisal2ThirdPartyType.Inspectify]: 'IN',
        }[type]
        identifier = `${prefix}-${item.thirdPartyAPI.orderID}`
      }
      if (type === Appraisal2ThirdPartyType.EliteValuation && item.thirdPartyAPI.OrderId)
        identifier = `EV-${item.thirdPartyAPI.FileId || item.thirdPartyAPI.OrderId}`
      if (type === Appraisal2ThirdPartyType.VisionVMC && item.thirdPartyAPI.appraisalFileNumber)
        identifier = `VV-${item.thirdPartyAPI.appraisalFileNumber}`
      if (type === Appraisal2ThirdPartyType.AMCDirect && item.thirdPartyAPI.appraisalFileNumber)
        identifier = `AD-${item.thirdPartyAPI.appraisalFileNumber}`
      if (type === Appraisal2ThirdPartyType.Arivs && item.thirdPartyAPI.appraisalID)
        identifier = `AR-${item.thirdPartyAPI.appraisalID}`
      if (type === Appraisal2ThirdPartyType.ClassValuation && item.thirdPartyAPI.appraisalID)
        identifier = `CV-${item.thirdPartyAPI.appraisalID}`
      if (identifier) {
        rlt.push(
          <div
            className="text-shade-blue mt-2 hover:underline cursor-pointer italic font-bold"
            onClick={() => {
              setEditOrderID('All')
              setThirdpartyType(type)
              setThirdpartyData(item)
              setCancelEditFlag(cancelEditFlag + 1)
            }}
          >
            {identifier}
          </div>,
        )
      }
      return <div key={2}>{rlt}</div>
    }

    const borrowerName = [feed.firstName, feed.lastName].filter(Boolean).join(' ') || feed.entityTitle || ''

    data.map((item) => {
      let orderType = item.info.orderType

      if (additionalTypes) {
        const integrationType = integrationTypeTitlesReversed[item.info.amc] as IntegrationType
        if (integrationType) {
          const newOrderType = additionalTypes?.[integrationType]?.[item.info.clientLenderAccount || 'main']?.orderType
          if (newOrderType && newOrderType[orderType]) orderType = newOrderType[orderType]
        }
      }

      let json: any = {
        ...item.info,
        propertyAddress:
          normalizePropertyAddresses(item.info?.propertyAddress)[0] ||
          normalizePropertyAddresses(feed.propertyAddresses)?.[0] ||
          feed.subjectPropertyAddress ||
          '',
        vendor: getVendorFragment(item),
        orderDueDate: `<span><div class="border-b w-full mb-2">${formatDate(item.info.orderDate)}</div>${formatDate(
          item.info.clientDueDate,
        )}</span>`,
        orderTypeForm: `<span><div class="border-b w-full mb-2">${orderType}${
          item.info.rush
            ? '<span class="ml-2 px-2 rounded bg-red-100 border-red-400 text-red-700 not-italic">Rush</span>'
            : ''
        }</div>${item.info.appraisalForm || ''}</span>`,
        status: hasInternalPermission ? renderStatus(item) : Appraisal2StatusType[item.status],
        refreshMessages: valueLinkVendorTitles.includes(item.info?.amc)
          ? item.info?.refreshMessages
            ? 'On'
            : 'Off'
          : '-',
        id: item.id,
        createdBy: `<div>${formatTime(item.createdAt)}</div><div>${item.createdBy}</div>`,
        inspectionScheduled: item.info.inspectionScheduled
          ? formatDate(item.info.inspectionScheduled)
          : item.info.inspectionDate
          ? formatDate(item.info.inspectionDate)
          : '',
        inspectionCompleted: item.info.inspectionCompleted ? formatDate(item.info.inspectionCompleted) : '',
        appraisalCost: item.info.appraisalCost ? `$${item.info.appraisalCost}` : '',
        paymentStatus: item.info.paymentStatus || '',
        loanNumber: feed.byteproFileName || feed.loanNumber || Api.getLoanNumber(),
        borrower: borrowerName,
        canUpdateRow:
          ['newOrder', 'onHold'].includes(item.status) ||
          (item.status === 'pendingPayment' && item.info?.amc === integrationTypeTitles[IntegrationType.AMCDirect]),
      }
      rlt.push(json)
    })
    return rlt
  }, [data, additionalTypes, feed, tableColumns, hasInternalPermission])

  useEffect(() => {
    let _orderIDs: any = ['All']
    data.map((item: any) => {
      // if (item.status !== 2) {
      _orderIDs.push(item.id)
      // }
    })
    setOrderIDs(_orderIDs)
  }, [data])

  const getCredentials = async () => {
    const loanNumber = Number(Api.getLoanNumber())
    const credentials: Record<IntegrationType, any[]> = {} as any
    const docTypes: Record<IntegrationType, string[]> = {} as any
    const jobTypes: Record<IntegrationType, string[]> = {} as any
    const additionalTypes: Record<IntegrationType, string[]> = {} as any
    const additionalInputs: Record<IntegrationType, any> = {} as any
    const errors: Record<IntegrationType, string> = {} as any

    const data: Record<
      IntegrationType,
      {
        credentials: any
        docTypes: string[]
        jobTypes: string[]
        additional: any
        additionalInputs: any
        error?: string
      }
    > = await getBorrowerEliteCredential(loanNumber)
    Object.keys(data).forEach((_integrationType) => {
      const integrationType = _integrationType as IntegrationType
      credentials[integrationType] = data[integrationType].credentials
      docTypes[integrationType] = data[integrationType].docTypes
      jobTypes[integrationType] = data[integrationType].jobTypes
      additionalTypes[integrationType] = data[integrationType].additional
      additionalInputs[integrationType] = data[integrationType].additionalInputs
      if (data[integrationType].error) errors[integrationType] = data[integrationType].error as string
    })
    setCredentials(credentials)
    setDocTypes(docTypes)
    setJobTypes(jobTypes)
    setAdditionalTypes(additionalTypes)
    setAdditionalInputs(additionalInputs)
    setVendorErrors(errors)
    return true
  }

  const initData = async (needReset = true, updated = false, currentId = 0, syncFacts = true) => {
    setLoading(true)
    const res = await getAppraisalInformationFeedV2({ syncFacts })
    setAppraiser(res.appraiserEmail)
    if (res.success) {
      setData(res.data)
      needReset && setFeed(res.feed)
      setLogs(res.logs)
      if (Array.isArray(res.tableColumns)) setTableColumns(res.tableColumns)
      if (updated) {
        const item = currentId ? res.data.find((v: any) => v.id == currentId) : res.data[res.data.length - 1]

        const type = item?.thirdPartyAPI.type
        if (Appraisal2ThirdPartyTypeForInit.includes(type)) {
          setThirdpartyType(type)
          setThirdpartyData(item)
        }
      }
    }
    setLoading(false)
  }

  const onRefreshVendorOrders = async () => {
    if (refreshingOrders || !loanNo) return
    setRefreshingOrders(true)
    try {
      const res = await refreshVendorAppraisalOrders()
      if (res?.success) {
        const errCount = Array.isArray(res.errors) ? res.errors.length : 0
        const skipped = Number(res.skipped) || 0
        // `updated` counts orders the vendor actually CHANGED, not orders we reached.
        // Nothing changing is the normal outcome and must not read like a failed refresh.
        const skipNote = skipped ? ` ${skipped} order(s) have no vendor order to check.` : ''
        if (errCount && res.updated === 0) {
          toast(`Vendor refresh finished with ${errCount} error(s).`, { type: 'warning' })
        } else if (errCount) {
          toast(`Updated ${res.updated} order(s); ${errCount} error(s).`, { type: 'warning' })
        } else if (res.total === 0) {
          toast('No vendor API orders to refresh.', { type: 'info' })
        } else if (res.updated === 0) {
          toast(`Checked ${res.total} vendor order(s), all already up to date.${skipNote}`, { type: 'success' })
        } else {
          toast(`Updated ${res.updated} of ${res.total} vendor order(s).${skipNote}`, { type: 'success' })
        }
        setRefreshSummary(res)
        setRefreshSummaryAt(Date.now())
        await initData(false, false, 0, false)
      } else {
        toast(res?.message || 'Failed to refresh vendor orders.', { type: 'error' })
      }
    } catch {
      toast('Failed to refresh vendor orders.', { type: 'error' })
    }
    setRefreshingOrders(false)
  }

  const initOverviewData = () => {
    const loanNumber = Api.getLoanNumber()
    loanOverviewAndStepLogic(loanNumber, '')
  }

  useEffect(() => {
    if (!loanNo) return
    initOverviewData()
    // No ValueLink fact-sync on load — it floods SOAP and blanks Notification Logs when the
    // vendor modal opens. Use the Orders "Refresh" button for vendor fact sync.
    initData(true, false, 0, false)
    getCredentials()
  }, [loanNo])

  const getDateWithoutDivider = (values: Record<string, any>) => {
    let rlt = cloneDeep(values)
    Object.keys(rlt).map((key) => {
      if (inputs[key]?.inputType === 'divider') {
        delete rlt[key]
      }
    })
    return rlt
  }

  const onCancel = () => {
    setUpdateChangeMade(false)
    setEditOrderID('All')
  }

  // A failed create still inserts the local order. Stay on that row with the values the user
  // entered so the next submit updates it. Reloading loan defaults here is what wiped the form.
  const keepOrderFormAfterFailure = async (orderId?: number | string) => {
    const id = Number(orderId)
    if (id) {
      setEditOrderID(String(id))
      ;(formRef.current as any)?.stayInEdit?.(id)
    }
    await initData(false, false, 0, false)
  }

  const onSubmit = (currentId: any, values: Record<string, any>, finishEditing = true) => {
    if (values.amc === 'Inspectify' && values.orderType === 'draw_inspection') {
      if (!values.accessType) {
        toast('Access Type is required for Inspectify draw inspection', { type: 'error' })
        return Promise.reject()
      }
      const needsCodeAndDirections = ['door_code', 'lockbox', 'combination'].includes(values.accessType)
      if (needsCodeAndDirections && !String(values.accessCode || '').trim()) {
        toast(`Access Code is required for access type "${values.accessType}"`, { type: 'error' })
        return Promise.reject()
      }
      if ((needsCodeAndDirections || values.accessType === 'other') && !String(values.accessDirections || '').trim()) {
        toast(`Access Directions are required for access type "${values.accessType}"`, { type: 'error' })
        return Promise.reject()
      }
      if (!currentId) {
        const scopeItems = Array.isArray(values.inspectionScopeItems) ? values.inspectionScopeItems : []
        const validScope = scopeItems.some(
          (row: any) => String(row?.name || '').trim() && String(row?.description || '').trim(),
        )
        if (!validScope) {
          toast('At least one scope item with name and description is required for draw inspection', { type: 'error' })
          return Promise.reject()
        }
      }
    }

    if (
      values.amc === integrationTypeTitles[IntegrationType.ClassValuation] &&
      values.orderType === CLASS_VALUATION_ANALYSIS_ORDER_TYPE
    ) {
      const docs = Array.isArray(values.documents) ? values.documents : []
      const hasUploadedDoc = docs.some((doc: any) => String(doc?.fileKey || '').trim())
      if (!hasUploadedDoc) {
        toast('Upload the original appraisal PDF for Class Valuation Analysis (CVA).', { type: 'error' })
        return Promise.reject()
      }
    }

    const propertyAddresses = normalizePropertyAddresses(values.propertyAddress)
    if (!propertyAddresses.length) {
      toast('Property Address is required!', { type: 'error' })
      return Promise.reject()
    }
    if (currentId && propertyAddresses.length > 1) {
      toast('Select exactly one Property Address when updating an order.', { type: 'error' })
      return Promise.reject()
    }
    if (values.amc === integrationTypeTitles[IntegrationType.AMCDirect] && !`${values.noteMessages || ''}`.trim()) {
      ;(formRef.current as any)?.updateInputs?.({
        noteMessages: { required: true, error: REQUIRED_FIELD_ERROR_MESSAGE },
      })
      toast('Note Messages is required for AMC Direct.', { type: 'error' })
      return Promise.reject()
    }

    const baseData = getDateWithoutDivider(values)
    // Never send AutoComplete chip arrays to the API / third parties
    delete baseData.propertyAddress
    if (values.amc === integrationTypeTitles[IntegrationType.AMCDirect]) {
      baseData.isSendInvoice = false
      baseData.notifications = []
    }

    return new Promise(async (resolve, reject) => {
      setUpdateChangeMade(false)
      setLoading(true)

      const onCreateAppraisal = async (res: Record<string, any>, address: string) => {
        values.id = res.data.id
        values.status = Appraisal2StatusType.newOrder
        values.propertyAddress = address
        values.createdBy = `<div>${formatTime(res.data.createdAt)}</div><div>${res.data.createdBy}</div>`
        handleFailedDocsNotification(res.thirdPartyResponse)
      }

      const onUpdateAppraisal = async (res: Record<string, any>, address: string) => {
        values.propertyAddress = address
        values.createdBy = `<div>${formatTime(res.data.createdAt)}</div><div>${res.data.createdBy}</div>`
        handleFailedDocsNotification(res.thirdPartyResponse)
      }

      try {
        if (!currentId) {
          let lastRes: Record<string, any> | null = null
          for (const address of propertyAddresses) {
            const payload = { ...cloneDeep(baseData), propertyAddress: address }
            const res = await createAppraisalInfoV2(payload)
            lastRes = res
            // Api already toasts res.message; reject so the form does not show "Successfully Updated".
            if (!res?.success) {
              setLoading(false)
              await keepOrderFormAfterFailure(res?.data?.id)
              reject(new Error(res?.message || 'AMC create failed'))
              return
            }
            await onCreateAppraisal(res, address)
          }
          setLoading(false)
          // Do not fact-sync here — that SOAP flood races GetOrderCommentList and blanks
          // Notification Logs in the vendor modal that opens from `updated=true`.
          await initData(finishEditing, true, lastRes?.data?.id || 0, false)
          resolve(values)
          setEditOrderID(finishEditing ? 'All' : String(lastRes?.data?.id || 'All'))
        } else {
          const address = propertyAddresses[0]
          const payload = { ...cloneDeep(baseData), propertyAddress: address }
          const res = await updateAppraisalInfoV2(currentId, payload)
          if (!res?.success) {
            setLoading(false)
            await keepOrderFormAfterFailure(currentId)
            reject(new Error(res?.message || 'AMC update failed'))
            return
          }
          await onUpdateAppraisal(res, address)
          setLoading(false)
          resolve(values)
          setEditOrderID(finishEditing ? 'All' : String(currentId))
          initData(true, true, currentId, false)
        }
      } catch (e) {
        setLoading(false)
        reject(e)
      }
    })
  }

  const onCloseModal = () => {
    setThirdpartyType(null)
    setThirdpartyData(null)
    // Light reload only (no ValueLink fact-sync). Full sync runs in the background on
    // page load / Refresh and must not race GetOrderCommentList on the next open.
    initData(true, false, 0, false)
  }

  const logFragment = useMemo(() => {
    let rlt: any = []
    // Blank Select ("- Select -") is "" — treat like All so notes never vanish.
    // A general note (no order id) stays visible for any order filter.
    const filterOrderId = editOrderID && editOrderID !== 'All' ? String(editOrderID) : null
    logs.map((item: any) => {
      let flag = true
      if (filterOrderId && item.id !== null && item.id !== undefined) {
        if (String(item.id) !== filterOrderId) flag = false
      }
      if (flag) {
        rlt.push(item)
      }
    })
    const hiddenCount = logs.length - rlt.length
    return (
      <div className="mb-6 overflow-auto rounded shadow-md">
        {hiddenCount > 0 && filterOrderId && (
          <div className="flex flex-wrap items-center gap-2 px-2 py-2 text-[13px] text-gray-600 bg-slate-50 border-b border-slate-200">
            <span>
              Showing notes for Order {filterOrderId} and the loan. {hiddenCount} note
              {hiddenCount === 1 ? '' : 's'} on other orders {hiddenCount === 1 ? 'is' : 'are'} hidden.
            </span>
            <span
              className="cursor-pointer font-medium text-shade-blue hover:underline"
              onClick={() => setEditOrderID('All')}
            >
              Show all
            </span>
          </div>
        )}
        <table className="w-full min-w-[520px] pl-6 text-left text-sm text-gray-900 dark:text-gray-400">
          <thead className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide bg-slate-50 border-b border-slate-200 dark:bg-gray-700 dark:text-gray-400">
            <tr>
              <th className="px-2 py-3">
                <span>Order</span>
                <br />
                <span>ID</span>
              </th>
              <th className="px-2 py-3">
                <span>Message</span>
                <br />
                <span>By/Date</span>
              </th>
            </tr>
          </thead>
          <tbody className="text-[14.5px] text-gray-900">
            {rlt.map((message: any, index: number) => (
              <tr key={index} className={`${index % 2 ? 'bg-gray-50' : ''}`}>
                <td className="p-2">{message.id}</td>
                <td className="p-2">
                  <div key={index} className="text-[14px]">
                    <div className="flex flex-wrap items-center gap-2">
                      - <span className="font-variation-settings-600">{message.email}</span>
                      {Object.keys(message.notifiers || {}).length > 0 && (
                        <span className="cursor-pointer">
                          <Tooltip
                            message={JSON.stringify(message.notifiers)
                              .split(',')
                              .join('\n')
                              .replace('{', '')
                              .replace('}', '')}
                          >
                            <IdentificationIcon className="w-[15px] h-[15px]"></IdentificationIcon>
                          </Tooltip>
                        </span>
                      )}
                      <span className="text-gray-500">{formatTime(message.time)}</span>
                    </div>
                    <div className="ml-2 mt-1 break-words">{message.value}</div>
                    {message.files?.length > 0 && (
                      <div className="flex flex-wrap gap-2 ml-2">
                        <span className="text-[13px] ">Files:</span>
                        {message.files?.map((file: BaseFile, index: number) => (
                          <span
                            key={index}
                            className="flex gap-1 items-center hover:underline cursor-pointer text-[15px] font-medium text-shade-blue"
                            onClick={() => openS3Document(file.fileKey)}
                          >
                            <span className="text-[13px]">{file.name}, </span>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }, [logs, editOrderID])

  const onRemove = async (id: any) => {
    return new Promise((resolve) => {
      setUpdateChangeMade(false)
      setLoading(true)
      deleteAppraisalInfoV2(id).then((res) => {
        if (res.success) {
          setLoading(false)
          resolve(true)
          setEditOrderID('All')
          initData()
        }
      })
    })
  }

  const onConfirmCancelOrder = async () => {
    const id = Number(editOrderID)
    setAction('cancellingOrder')
    const res = await cancelAppraisalInfoV2({ id })
    if (res.success) {
      let temp = cloneDeep(logs)
      temp.push(res.data)
      setLogs(temp)
    } else if (res?.message) {
      toast(res.message, { type: 'error' })
    }
    setAction('')

    initData()

    return true
  }

  const renderEditOrderInput = () => {
    return (
      <Select
        className="min-w-[160px]"
        id={'orderID'}
        title={'Order ID'}
        options={orderIDs}
        key={'ordeID'}
        value={editOrderID || 'All'}
        hasDefaultOption={false}
        onChange={(value) => setEditOrderID(value || 'All')}
      />
    )
  }

  const onChangeFiles = (documents: DocumentFile[]) => {
    setDocFiles(documents)
  }

  return (
    <div className="OrderAppraisals2-container px-1 sm:px-2">
      {!child && <Overview title="Order Valuation" />}
      <div className="max-w-screen-2xl m-auto mb-6">
        <div className={`relative bg-white ${child ? '' : 'mb-4 rounded p-2 shadow1 sm:p-4'}`}>
          <LayoutLoading show={loading} />
          <div className="relative my-2 mb-5 flex items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-xl font-variation-settings-600 sm:ml-2 sm:text-2xl">
              Order Valuation
              <span className="text-[14px] ml-3">
                {loading && <img src={svgLoading} className="inline w-6 h-6 text-white animate-spin" />}
              </span>
            </div>
            <Button color="gray" link className="py-[5px]" onClick={() => setShowOrderProcess((open) => !open)}>
              {showOrderProcess ? 'Hide Order Process' : 'Show Order Process'}
            </Button>
          </div>
          {Object.keys(inputs).length > 0 && (
            <div className="grid gap-5 md:grid-cols-3 grid-cols-1 mt-4">
              {showOrderProcess && (
                <div className="md:col-span-1 h-fit">
                  <div className="border-b font-variation-settings-[600] ml-2 flex flex-wrap items-end gap-2">
                    <span>Order Process</span>
                    <span>
                      <div className="-mb-3">{renderEditOrderInput()}</div>
                    </span>
                  </div>
                  {logFragment}
                  <div className="px-3">
                    {renderEditOrderInput()}
                    <TextArea
                      className="pt-1"
                      title={'Message'}
                      key={'appraisalLog'}
                      rows={3}
                      value={editingLog}
                      onChange={setEditingLog}
                    />
                    <InputFileTable
                      className="pt-1 mb-4"
                      title={'Files'}
                      filePath={`loan/${Api.getLoanNumber()}/orderValuation`}
                      key={'commetFiles'}
                      name={'commetFiles'}
                      acceptFileTypes={'application/pdf'}
                      value={docFiles}
                      onChange={(value) => onChangeFiles(value)}
                      showHistory={() => {}}
                      showCategory={false}
                      showStatus={false}
                      multiple={true}
                    />
                    <div className="mt-4 flex flex-col justify-center gap-3 sm:flex-row sm:flex-wrap sm:gap-4">
                      <Button
                        className="w-full py-[5px] sm:w-auto"
                        onClick={() => createLog(false)}
                        loading={action === 'createLog'}
                      >
                        Send Message
                      </Button>
                      <Button
                        color="gray"
                        className="w-full py-[5px] sm:w-auto"
                        onClick={cancelOrder}
                        loading={action === 'cancellingOrder'}
                      >
                        Cancel Order
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              <div className={`${showOrderProcess ? 'md:col-span-2' : 'md:col-span-3'} h-fit`}>
                <div className="border-b font-variation-settings-[600] ml-2 flex items-center justify-between pr-2">
                  <span>Orders</span>
                  <button
                    type="button"
                    onClick={onRefreshVendorOrders}
                    disabled={refreshingOrders || loading}
                    className="inline-flex items-center gap-1 text-sm text-shade-blue disabled:opacity-50"
                    title="Refresh status and dates from vendor APIs"
                  >
                    <ArrowPathIcon className={`w-4 h-4 ${refreshingOrders ? 'animate-spin' : ''}`} />
                    Refresh
                  </button>
                </div>
                <div className={showOrderProcess ? 'border-l' : ''}>
                  <FormTable
                    ref={formRef}
                    cols={4}
                    header={getHeader}
                    inputs={inputs}
                    inputLogic={inputLogic}
                    data={getData}
                    onSubmit={onSubmit}
                    onRemove={onRemove}
                    onCancel={onCancel}
                    onAddPress={onAddPress}
                    type={'orderAppraisal'}
                    permission={
                      hasInternalPermission ? (hasCreateOrderPermission ? 1 : 4) : hasCreateOrderPermission ? 2 : 3
                    }
                    addText={'Click Here to add New Order'}
                    cancelEditFlag={cancelEditFlag}
                    showFieldHistory={showHistory}
                    canOverride={hasPermission('Loan.Appraisals.Override')}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
        {thirdpartyType && AppraisalScopeThirdpartyTypes.includes(thirdpartyType) && (
          <AppraisalNation
            onClose={onCloseModal}
            data={thirdpartyData}
            docTypes={docTypes?.[appraisal2IntegrationTypes[thirdpartyType]]}
            integrationType={appraisal2IntegrationTypes[thirdpartyType]}
          />
        )}
        {thirdpartyType == Appraisal2ThirdPartyType.EliteValuation && (
          <EliteValuation onClose={onCloseModal} data={thirdpartyData} />
        )}
        {thirdpartyType == Appraisal2ThirdPartyType.Trinity && <Trinity onClose={onCloseModal} data={thirdpartyData} />}
        {thirdpartyType == 'Inspectify' && <Inspectify onClose={onCloseModal} data={thirdpartyData} />}
        {thirdpartyType &&
          [Appraisal2ThirdPartyType.Arivs, Appraisal2ThirdPartyType.ClassValuation].includes(thirdpartyType) && (
            <StandardAppraisalModal
              integrationType={
                thirdpartyType === Appraisal2ThirdPartyType.Arivs
                  ? IntegrationType.Arivs
                  : IntegrationType.ClassValuation
              }
              docTypes={
                thirdpartyType === Appraisal2ThirdPartyType.ClassValuation
                  ? docTypes?.[IntegrationType.ClassValuation]
                  : undefined
              }
              onClose={onCloseModal}
              data={thirdpartyData}
            />
          )}
      </div>
      <Prompt
        when={updateChangeMade}
        message={`You have added documents and made changes.\nAre you sure you want to leave without clicking the 'Update' button?`}
      />
      <RefreshResults
        summary={refreshSummary}
        lastUpdatedAt={refreshSummaryAt}
        onClose={() => setRefreshSummary(null)}
      />
      <Modal
        button={<span></span>}
        title={`Send Message to`}
        titleOkay="Confirm"
        titleCancel="Close"
        isOpen={messageToModal}
        lastUpdatedAt={Date.now()}
        loading={action === 'createLog'}
        onOk={() => createLog(true)}
        onClose={() => setMessageToModal(false)}
      >
        <div className="w-[calc(100vw-5rem)] max-w-[560px]">
          <LoanPartiesEmailTo
            loanNumber={Number(Api.getLoanNumber())}
            value={notifiers}
            data={toEmails}
            onChange={onChangeEmail}
            loadParties
            modalId="orderAppraisalInformation"
          />
        </div>
      </Modal>
    </div>
  )
}
